import React from 'react';
import { Tooltip } from 'antd';
import { TrendingUp, TrendingDown } from 'lucide-react';

const formatSymbols = (symbols) => {
  if (!symbols?.length) return "";

  const displayedSymbols = symbols.slice(0, 3).map((ticker, index) => (
    <a
      key={index}
      href={`https://finance.yahoo.com/quote/${ticker.toUpperCase()}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:text-blue-800"
      onClick={(e) => e.stopPropagation()}
    >
      {ticker.toUpperCase()}
    </a>
  ));

  const remainingCount = symbols.length > 3 ? ` ...+${symbols.length - 3}` : "";

  return (
    <>
      {displayedSymbols.reduce((prev, curr) => [prev, ", ", curr])}
      {remainingCount}
    </>
  );
};

const NewsArticle = ({ article }) => {
  const {
    title,
    image_url,
    description,
    published_utc,
    publisher,
    article_url,
    insights,
    metadata: { symbols = [] } = {},
  } = article;

  const sentimentData = insights?.find((insight) => insight.sentiment);
  const sentiment = sentimentData?.sentiment;
  const sentimentReasoning = sentimentData?.sentiment_reasoning;
  const publisherLogo = publisher?.logo_url || image_url;

  return (
    <div
      onClick={() => window.open(article_url, "_blank")}
      className="group flex items-center gap-3 px-3 py-8 cursor-pointer bg-white hover:bg-gray-50 transition-colors duration-200 border-b border-gray-200"
    >
      {/* Image container */}
      <div className="flex-shrink-0">
        <div
          className="relative w-32 rounded-lg shadow-md overflow-hidden"
          style={{ background: "rgba(0,0,0,0.3)" }}
        >
          <img
            src={image_url}
            alt={title}
            className="object-cover w-full h-full"
            loading="lazy"
            onError={(e) => {
              e.target.src = "/api/placeholder/96/96";
            }}
          />
        </div>
      </div>

      {/* Content container */}
      <div className="flex flex-col flex-grow px-6 min-w-0">
        {/* Title and description */}
        <div className="space-y-1">
          <h3 className="font-semibold text-gray-900 text-sm leading-5 line-clamp-2">
            {title}
          </h3>
          <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
            {description}
          </p>
        </div>

        {/* Metadata footer */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
          <span>
            {new Date(published_utc).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>

          {sentiment && (
            <Tooltip
              title={sentimentReasoning}
              mouseEnterDelay={0.5}
              overlayStyle={{ maxWidth: 300 }}
            >
              <span>
                {sentiment === "positive" ? (
                  <TrendingUp className="w-4 h-4 text-green-500" />
                ) : sentiment === "negative" ? (
                  <TrendingDown className="w-4 h-4 text-red-600" />
                ) : null}
              </span>
            </Tooltip>
          )}

          {symbols.length > 0 && (
            <>
              <span className="text-gray-400">•</span>
              <span className="truncate">{formatSymbols(symbols)}</span>
            </>
          )}

          <img
            src={publisherLogo}
            alt={publisher?.name}
            className="h-6 w-6 object-contain rounded"
            onError={(e) => {
              e.target.src = "/api/placeholder/16/16";
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(NewsArticle);
